// ===========================================
// FONT DEMO PAGE - ฿ Symbol Display
// ===========================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Common web-safe and Google Fonts that render ฿ well
const FONTS = [
  // Sans-serif
  { name: 'Arial', family: 'Arial, sans-serif' },
  { name: 'Helvetica', family: 'Helvetica, sans-serif' },
  { name: 'Verdana', family: 'Verdana, sans-serif' },
  { name: 'Tahoma', family: 'Tahoma, sans-serif' },
  { name: 'Trebuchet MS', family: '"Trebuchet MS", sans-serif' },
  { name: 'Segoe UI', family: '"Segoe UI", sans-serif' },
  { name: 'Roboto', family: 'Roboto, sans-serif' },
  { name: 'Open Sans', family: '"Open Sans", sans-serif' },
  { name: 'Lato', family: 'Lato, sans-serif' },
  { name: 'Montserrat', family: 'Montserrat, sans-serif' },
  { name: 'Poppins', family: 'Poppins, sans-serif' },
  { name: 'Inter', family: 'Inter, sans-serif' },
  { name: 'Nunito', family: 'Nunito, sans-serif' },
  { name: 'Source Sans Pro', family: '"Source Sans Pro", sans-serif' },
  { name: 'Ubuntu', family: 'Ubuntu, sans-serif' },
  { name: 'Oswald', family: 'Oswald, sans-serif' },
  { name: 'Raleway', family: 'Raleway, sans-serif' },
  { name: 'PT Sans', family: '"PT Sans", sans-serif' },
  { name: 'Noto Sans', family: '"Noto Sans", sans-serif' },
  { name: 'Noto Sans Thai', family: '"Noto Sans Thai", sans-serif' },

  // Serif
  { name: 'Times New Roman', family: '"Times New Roman", serif' },
  { name: 'Georgia', family: 'Georgia, serif' },
  { name: 'Palatino', family: '"Palatino Linotype", Palatino, serif' },
  { name: 'Playfair Display', family: '"Playfair Display", serif' },
  { name: 'Merriweather', family: 'Merriweather, serif' },
  { name: 'Lora', family: 'Lora, serif' },

  // Monospace
  { name: 'Courier New', family: '"Courier New", monospace' },
  { name: 'Consolas', family: 'Consolas, monospace' },
  { name: 'Monaco', family: 'Monaco, monospace' },
  { name: 'Fira Code', family: '"Fira Code", monospace' },
  { name: 'JetBrains Mono', family: '"JetBrains Mono", monospace' },
  { name: 'Source Code Pro', family: '"Source Code Pro", monospace' },

  // Display/Decorative
  { name: 'Impact', family: 'Impact, sans-serif' },
  { name: 'Bebas Neue', family: '"Bebas Neue", sans-serif' },
  { name: 'Anton', family: 'Anton, sans-serif' },
  { name: 'Russo One', family: '"Russo One", sans-serif' },
  { name: 'Bangers', family: 'Bangers, cursive' },
  { name: 'Permanent Marker', family: '"Permanent Marker", cursive' },
  { name: 'Pacifico', family: 'Pacifico, cursive' },
  { name: 'Lobster', family: 'Lobster, cursive' },

  // Casino/Gaming style
  { name: 'Casino', family: 'Casino, fantasy' },
  { name: 'Copperplate', family: 'Copperplate, fantasy' },
  { name: 'Stencil', family: 'Stencil, fantasy' },
];

const SIZES = [16, 20, 24, 32, 48, 64, 96];
const WEIGHTS = [
  { label: 'Light', value: 300 },
  { label: 'Normal', value: 400 },
  { label: 'Medium', value: 500 },
  { label: 'Semi-Bold', value: 600 },
  { label: 'Bold', value: 700 },
  { label: 'Extra-Bold', value: 800 },
];

export function FontDemoPage() {
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState(48);
  const [selectedWeight, setSelectedWeight] = useState(700);
  const [sampleText, setSampleText] = useState('฿1,000,000');
  const [showAll, setShowAll] = useState(true);
  const [favorites, setFavorites] = useState<string[]>([]);

  const toggleFavorite = (fontName: string) => {
    setFavorites(prev =>
      prev.includes(fontName)
        ? prev.filter(f => f !== fontName)
        : [...prev, fontName]
    );
  };

  const displayFonts = showAll ? FONTS : FONTS.filter(f => favorites.includes(f.name));

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      {/* Load Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700;800&family=Open+Sans:wght@300;400;500;600;700;800&family=Lato:wght@300;400;700&family=Montserrat:wght@300;400;500;600;700;800&family=Poppins:wght@300;400;500;600;700;800&family=Inter:wght@300;400;500;600;700;800&family=Nunito:wght@300;400;500;600;700;800&family=Source+Sans+Pro:wght@300;400;600;700&family=Ubuntu:wght@300;400;500;700&family=Oswald:wght@300;400;500;600;700&family=Raleway:wght@300;400;500;600;700;800&family=PT+Sans:wght@400;700&family=Noto+Sans:wght@300;400;500;600;700;800&family=Noto+Sans+Thai:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700;800&family=Merriweather:wght@300;400;700&family=Lora:wght@400;500;600;700&family=Fira+Code:wght@300;400;500;600;700&family=JetBrains+Mono:wght@300;400;500;600;700;800&family=Source+Code+Pro:wght@300;400;500;600;700&family=Bebas+Neue&family=Anton&family=Russo+One&family=Bangers&family=Permanent+Marker&family=Pacifico&family=Lobster&display=swap"
        rel="stylesheet"
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-yellow-400">฿ Symbol Font Demo</h1>
            <p className="text-gray-400 mt-1">Compare the Thai Baht symbol across different fonts</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Back to Home
          </button>
        </div>

        {/* Controls */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Sample Text */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Sample Text</label>
              <input
                type="text"
                value={sampleText}
                onChange={(e) => setSampleText(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="฿1,000,000"
              />
            </div>

            {/* Size */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Size: {selectedSize}px</label>
              <select
                value={selectedSize}
                onChange={(e) => setSelectedSize(Number(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                {SIZES.map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
            </div>

            {/* Weight */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Weight</label>
              <select
                value={selectedWeight}
                onChange={(e) => setSelectedWeight(Number(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                {WEIGHTS.map(w => (
                  <option key={w.value} value={w.value}>{w.label} ({w.value})</option>
                ))}
              </select>
            </div>

            {/* Filter */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Filter</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAll(true)}
                  className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                    showAll ? 'bg-yellow-500 text-gray-900' : 'bg-gray-700 text-white'
                  }`}
                >
                  All ({FONTS.length})
                </button>
                <button
                  onClick={() => setShowAll(false)}
                  className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                    !showAll ? 'bg-yellow-500 text-gray-900' : 'bg-gray-700 text-white'
                  }`}
                >
                  Favorites ({favorites.length})
                </button>
              </div>
            </div>
          </div>

          {/* Quick samples */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm text-gray-400">Quick samples:</span>
            {['฿', '฿100', '฿1,000', '฿10,000', '฿100,000', '฿1,000,000'].map(sample => (
              <button
                key={sample}
                onClick={() => setSampleText(sample)}
                className="px-2 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                {sample}
              </button>
            ))}
          </div>
        </div>

        {/* Color Variants Preview */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-yellow-400 mb-4">Color Variants</h2>
          <div className="flex flex-wrap gap-4">
            {[
              { bg: 'bg-green-600', text: 'text-white', label: 'Green (Balance)' },
              { bg: 'bg-yellow-500', text: 'text-gray-900', label: 'Gold (Premium)' },
              { bg: 'bg-gray-700', text: 'text-yellow-400', label: 'Dark + Gold' },
              { bg: 'bg-red-600', text: 'text-white', label: 'Red (Loss)' },
              { bg: 'bg-blue-600', text: 'text-white', label: 'Blue (Info)' },
              { bg: 'bg-purple-600', text: 'text-white', label: 'Purple' },
              { bg: 'bg-gradient-to-r from-yellow-400 to-orange-500', text: 'text-gray-900', label: 'Gold Gradient' },
            ].map(variant => (
              <div
                key={variant.label}
                className={`${variant.bg} ${variant.text} px-4 py-2 rounded-lg font-bold`}
                style={{ fontSize: selectedSize * 0.6, fontWeight: selectedWeight }}
              >
                {sampleText}
                <span className="block text-xs opacity-70 font-normal">{variant.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Font Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayFonts.map(font => (
            <div
              key={font.name}
              className={`bg-gray-800 rounded-xl p-4 border-2 transition-colors cursor-pointer ${
                favorites.includes(font.name)
                  ? 'border-yellow-500'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
              onClick={() => toggleFavorite(font.name)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">{font.name}</span>
                <span className={`text-lg ${favorites.includes(font.name) ? 'text-yellow-400' : 'text-gray-600'}`}>
                  {favorites.includes(font.name) ? '★' : '☆'}
                </span>
              </div>

              <div
                className="text-white truncate"
                style={{
                  fontFamily: font.family,
                  fontSize: selectedSize,
                  fontWeight: selectedWeight,
                  lineHeight: 1.2,
                }}
              >
                {sampleText}
              </div>

              <div className="mt-2 text-xs text-gray-500 font-mono truncate">
                {font.family}
              </div>
            </div>
          ))}
        </div>

        {/* CSS Reference */}
        <div className="mt-8 bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-yellow-400 mb-4">CSS Reference</h2>
          <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre className="text-green-400">
{`/* Recommended: Noto Sans Thai for best ฿ rendering */
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@400;700&display=swap');

.currency {
  font-family: 'Noto Sans Thai', 'Noto Sans', sans-serif;
  font-weight: ${selectedWeight};
  font-size: ${selectedSize}px;
}

/* The ฿ symbol is U+0E3F (Thai Baht Sign) */
/* Some fonts may not render it properly */`}
            </pre>
          </div>
        </div>

        {/* Info */}
        <div className="mt-8 bg-gray-800/50 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-400 mb-2">About the ฿ Symbol</h2>
          <ul className="text-gray-500 text-sm space-y-1">
            <li>• Unicode: U+0E3F (Thai Baht Sign)</li>
            <li>• HTML Entity: &amp;#3647; or &amp;#x0E3F;</li>
            <li>• The symbol is part of the Thai script block</li>
            <li>• Not all fonts include Thai characters - fallback fonts may render differently</li>
            <li>• Click any font card to add/remove from favorites</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default FontDemoPage;
